/**
 * The company profile's vocabularies — what onboarding offers, what the
 * organisation settings show, and what enrichment may write. (BW-58)
 *
 * The organisation view printed each stored value as it was, underscores
 * swapped for spaces ("Hr People", "Technology", "established program"), so a
 * pt-PT admin read their own answers in English. The options lived only inside
 * the onboarding steps that offered them, some with label keys and some — the
 * industries, the company sizes and the role types — as English literals.
 *
 * Each vocabulary here is the stored values `as const`, plus a label-key map
 * `satisfies Record<Value, string>`: onboarding and the organisation view draw
 * both from it, and the locale coverage spec proves every value is labelled.
 * Industry and role-type values are English words because that is what was
 * stored (and what the Brain and Brandfetch mapping write); they are
 * identifiers here, never shown.
 */

export const COMPANY_INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Finance',
  'Manufacturing',
  'Retail',
  'Hospitality',
  'Construction',
  'Education',
  'Consulting',
  'Logistics',
  'Energy',
  'Telecommunications',
  'Media & Entertainment',
  'Real Estate',
  'Other',
] as const;
export type CompanyIndustry = (typeof COMPANY_INDUSTRIES)[number];

export function isCompanyIndustry(value: unknown): value is CompanyIndustry {
  return typeof value === 'string' && (COMPANY_INDUSTRIES as readonly string[]).includes(value);
}

export const COMPANY_INDUSTRY_LABEL_KEY = {
  Technology: 'business.companyProfile.industry.technology',
  Healthcare: 'business.companyProfile.industry.healthcare',
  Finance: 'business.companyProfile.industry.finance',
  Manufacturing: 'business.companyProfile.industry.manufacturing',
  Retail: 'business.companyProfile.industry.retail',
  Hospitality: 'business.companyProfile.industry.hospitality',
  Construction: 'business.companyProfile.industry.construction',
  Education: 'business.companyProfile.industry.education',
  Consulting: 'business.companyProfile.industry.consulting',
  Logistics: 'business.companyProfile.industry.logistics',
  Energy: 'business.companyProfile.industry.energy',
  Telecommunications: 'business.companyProfile.industry.telecommunications',
  'Media & Entertainment': 'business.companyProfile.industry.mediaEntertainment',
  'Real Estate': 'business.companyProfile.industry.realEstate',
  Other: 'business.companyProfile.industry.other',
} as const satisfies Record<CompanyIndustry, string>;

/** Head-count bands; drawn as "{{range}} employees". */
export const COMPANY_SIZES = ['1-50', '51-200', '201-500', '501-1000', '1000+'] as const;
export type CompanySize = (typeof COMPANY_SIZES)[number];

export const CONTACT_ROLES = [
  'hr_people',
  'talent_acquisition',
  'mobility_manager',
  'founder_exec',
  'other',
] as const;
export type ContactRole = (typeof CONTACT_ROLES)[number];

export const CONTACT_ROLE_LABEL_KEY = {
  hr_people: 'business.businessOnboarding.roles.hrPeople',
  talent_acquisition: 'business.businessOnboarding.roles.talentAcquisition',
  mobility_manager: 'business.businessOnboarding.roles.mobilityManager',
  founder_exec: 'business.businessOnboarding.roles.founderExec',
  other: 'business.common.other',
} as const satisfies Record<ContactRole, string>;

export const PRIMARY_USE_CASES = [
  'international_hiring',
  'global_expansion',
  'diversity_inclusion',
  'talent_shortage',
  'cost_optimization',
  'remote_to_relocation',
] as const;
export type PrimaryUseCase = (typeof PRIMARY_USE_CASES)[number];

export const PRIMARY_USE_CASE_LABEL_KEY = {
  international_hiring: 'business.businessOnboarding.hiringContext.useCases.internationalHiring',
  global_expansion: 'business.businessOnboarding.hiringContext.useCases.globalExpansion',
  diversity_inclusion: 'business.businessOnboarding.hiringContext.useCases.diversity',
  talent_shortage: 'business.businessOnboarding.hiringContext.useCases.talentShortage',
  cost_optimization: 'business.businessOnboarding.hiringContext.useCases.costOptimization',
  remote_to_relocation: 'business.businessOnboarding.hiringContext.useCases.remoteToRelocation',
} as const satisfies Record<PrimaryUseCase, string>;

export const PRIMARY_USE_CASE_DESC_KEY = {
  international_hiring:
    'business.businessOnboarding.hiringContext.useCases.internationalHiringDesc',
  global_expansion: 'business.businessOnboarding.hiringContext.useCases.globalExpansionDesc',
  diversity_inclusion: 'business.businessOnboarding.hiringContext.useCases.diversityDesc',
  talent_shortage: 'business.businessOnboarding.hiringContext.useCases.talentShortageDesc',
  cost_optimization: 'business.businessOnboarding.hiringContext.useCases.costOptimizationDesc',
  remote_to_relocation: 'business.businessOnboarding.hiringContext.useCases.remoteToRelocationDesc',
} as const satisfies Record<PrimaryUseCase, string>;

/** Annual hiring volume bands; drawn as "{{range}} hires". */
export const ANNUAL_HIRING_VOLUMES = ['1-10', '11-50', '51-100', '100+'] as const;
export type AnnualHiringVolume = (typeof ANNUAL_HIRING_VOLUMES)[number];

export const ANNUAL_HIRING_VOLUME_DESC_KEY = {
  '1-10': 'business.businessOnboarding.hiringContext.volumes.starting',
  '11-50': 'business.businessOnboarding.hiringContext.volumes.growing',
  '51-100': 'business.businessOnboarding.hiringContext.volumes.established',
  '100+': 'business.businessOnboarding.hiringContext.volumes.enterprise',
} as const satisfies Record<AnnualHiringVolume, string>;

export const HIRING_TIMEFRAMES = ['immediate', '1-3months', '3-6months', '6-12months'] as const;
export type HiringTimeframe = (typeof HIRING_TIMEFRAMES)[number];

export const HIRING_TIMEFRAME_LABEL_KEY = {
  immediate: 'business.onboarding.hiringTimeframes.immediate',
  '1-3months': 'business.onboarding.hiringTimeframes.short',
  '3-6months': 'business.onboarding.hiringTimeframes.medium',
  '6-12months': 'business.onboarding.hiringTimeframes.long',
} as const satisfies Record<HiringTimeframe, string>;

export const MOBILITY_MATURITIES = [
  'first_time',
  'some_experience',
  'established_program',
] as const;
export type MobilityMaturity = (typeof MOBILITY_MATURITIES)[number];

export const MOBILITY_MATURITY_LABEL_KEY = {
  first_time: 'business.onboarding.maturity.firstTime',
  some_experience: 'business.onboarding.maturity.someExperience',
  established_program: 'business.onboarding.maturity.established',
} as const satisfies Record<MobilityMaturity, string>;

export const HIRING_GOALS = [
  'reduce_time_to_hire',
  'improve_candidate_experience',
  'increase_retention',
  'streamline_compliance',
  'track_mobility_roi',
  'reduce_relocation_costs',
  'build_talent_pipeline',
  'improve_onboarding',
] as const;
export type HiringGoal = (typeof HIRING_GOALS)[number];

export const HIRING_GOAL_LABEL_KEY = {
  reduce_time_to_hire: 'business.onboarding.hiringGoals.reduceTime',
  improve_candidate_experience: 'business.onboarding.hiringGoals.improveExperience',
  increase_retention: 'business.onboarding.hiringGoals.increaseRetention',
  streamline_compliance: 'business.onboarding.hiringGoals.streamlineCompliance',
  track_mobility_roi: 'business.onboarding.hiringGoals.trackRoi',
  reduce_relocation_costs: 'business.onboarding.hiringGoals.reduceCosts',
  build_talent_pipeline: 'business.onboarding.hiringGoals.buildPipeline',
  improve_onboarding: 'business.onboarding.hiringGoals.improveOnboarding',
} as const satisfies Record<HiringGoal, string>;

export const HIRING_CHALLENGES = [
  'immigration_visa',
  'finding_candidates',
  'candidate_dropoff',
  'post_arrival',
  'compliance_legal',
  'cost_management',
  'tracking_reporting',
] as const;
export type HiringChallenge = (typeof HIRING_CHALLENGES)[number];

export const HIRING_CHALLENGE_LABEL_KEY = {
  immigration_visa: 'business.onboarding.challenges.immigrationVisa',
  finding_candidates: 'business.onboarding.challenges.findingCandidates',
  candidate_dropoff: 'business.onboarding.challenges.candidateDropoff',
  post_arrival: 'business.onboarding.challenges.postArrival',
  compliance_legal: 'business.onboarding.challenges.complianceLegal',
  cost_management: 'business.onboarding.challenges.costManagement',
  tracking_reporting: 'business.onboarding.challenges.trackingReporting',
} as const satisfies Record<HiringChallenge, string>;

export const TARGET_ROLE_TYPES = [
  'Engineering',
  'Product',
  'Design',
  'Data Science',
  'DevOps / Infrastructure',
  'Marketing',
  'Sales',
  'Customer Success',
  'Finance',
  'HR / People',
  'Operations',
  'Executive / Leadership',
  'Other',
] as const;
export type TargetRoleType = (typeof TARGET_ROLE_TYPES)[number];

export const TARGET_ROLE_TYPE_LABEL_KEY = {
  Engineering: 'business.companyProfile.roleType.engineering',
  Product: 'business.companyProfile.roleType.product',
  Design: 'business.companyProfile.roleType.design',
  'Data Science': 'business.companyProfile.roleType.dataScience',
  'DevOps / Infrastructure': 'business.companyProfile.roleType.devops',
  Marketing: 'business.companyProfile.roleType.marketing',
  Sales: 'business.companyProfile.roleType.sales',
  'Customer Success': 'business.companyProfile.roleType.customerSuccess',
  Finance: 'business.companyProfile.roleType.finance',
  'HR / People': 'business.companyProfile.roleType.hrPeople',
  Operations: 'business.companyProfile.roleType.operations',
  'Executive / Leadership': 'business.companyProfile.roleType.executive',
  Other: 'business.companyProfile.roleType.other',
} as const satisfies Record<TargetRoleType, string>;

export const BUSINESS_TYPES = ['startup', 'scaleup', 'enterprise', 'agency'] as const;
export type BusinessType = (typeof BUSINESS_TYPES)[number];

export const BUSINESS_TYPE_LABEL_KEY = {
  startup: 'business.businessOnboarding.companyProfile.businessTypes.startup',
  scaleup: 'business.businessOnboarding.companyProfile.businessTypes.scaleup',
  enterprise: 'business.businessOnboarding.companyProfile.businessTypes.enterprise',
  agency: 'business.businessOnboarding.companyProfile.businessTypes.agency',
} as const satisfies Record<BusinessType, string>;

/** A stored value's label key, or null when the value is not in the vocabulary. */
export function vocabularyLabelKey<V extends string>(
  map: Readonly<Record<V, string>>,
  value: string | null | undefined
): string | null {
  return value && Object.prototype.hasOwnProperty.call(map, value) ? map[value as V] : null;
}
